import { Link } from 'react-router-dom';

const localServices = [
  {
    icon: 'ri-camera-fill',
    title: 'Security Camera Installation NYC',
    description: 'HD and 4K security camera systems installed across all New York City boroughs. Indoor, outdoor, PTZ, and covert cameras for homes and businesses.',
    highlights: ['Indoor', 'Outdoor', 'PTZ Cameras'],
    image: 'https://public.readdy.ai/ai/img_res/f64edfc05cbaddf7cee006959d35c4b4.jpg',
  },
  {
    icon: 'ri-vidicon-fill',
    title: 'CCTV Surveillance Systems New York',
    description: 'Complete CCTV system design and professional installation for NYC businesses, retail stores, restaurants, offices, and apartment buildings.',
    highlights: ['NYC Code Compliant', 'Commercial Grade'],
    image: 'https://readdy.ai/api/search-image?query=professional%20CCTV%20surveillance%20monitor%20screen%20security%20system%20New%20York%20office%20bright%20clean%20modern%20professional%20monitoring%20station%20cameras&width=600&height=380&seq=nyc-cctv1&orientation=landscape',
  },
  {
    icon: 'ri-door-lock-fill',
    title: 'Access Control Systems NYC',
    description: 'Keypad, card reader, and fob-based access control for NYC office buildings, co-ops, condos, and commercial properties including buzz-in system upgrades.',
    highlights: ['Offices', 'Condos', 'Co-ops'],
    image: 'https://readdy.ai/api/search-image?query=electronic%20access%20control%20system%20door%20entry%20keypad%20New%20York%20office%20building%20modern%20clean%20professional%20installation%20bright%20interior&width=600&height=380&seq=nyc-access1&orientation=landscape',
  },
  {
    icon: 'ri-home-smart-fill',
    title: 'Smart Automation New York',
    description: 'Smart home and business automation installation across NYC — smart locks, video doorbells, integrated security, and full automation systems.',
    highlights: ['Smart Locks', 'Doorbells', 'Automation'],
    image: 'https://readdy.ai/api/search-image?query=smart%20home%20automation%20device%20video%20doorbell%20NYC%20apartment%20modern%20residential%20technology%20bright%20clean%20installation%20professional&width=600&height=380&seq=nyc-smart1&orientation=landscape',
  },
];

const nycStats = [
  { value: '1-2hr', label: 'On-Site NYC Response', icon: 'ri-timer-flash-fill' },
  { value: '5', label: 'Boroughs Covered', icon: 'ri-map-pin-fill' },
  { value: '200+', label: 'NYC Installs Done', icon: 'ri-camera-fill' },
  { value: 'Same-Day', label: 'Service Available', icon: 'ri-tools-fill' },
];

export default function LocalServices() {
  return (
    <section className="bg-white py-24 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-slate-100" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full text-orange-700 text-xs font-bold uppercase tracking-[0.2em] border border-orange-200 bg-orange-50 mb-4">
            New York City
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-black text-slate-900 mb-4">
            Security System Installation in New York City
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">
            Local security camera installers serving all five boroughs. Fast response, expert installation,
            and systems designed for New York&apos;s unique residential and commercial environments.
          </p>
        </div>

        {/* NYC Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {nycStats.map((stat) => (
            <div key={stat.label} className="rounded-xl border border-orange-200 bg-orange-50 p-5 text-center">
              <div className="w-9 h-9 flex items-center justify-center mx-auto mb-2">
                <i className={`${stat.icon} text-orange-600 text-xl`} />
              </div>
              <p className="text-slate-900 font-display font-black text-2xl">{stat.value}</p>
              <p className="text-slate-500 text-xs mt-1 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Service cards — with images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {localServices.map((service) => (
            <div
              key={service.title}
              className="group rounded-2xl border border-slate-200 bg-white hover:border-orange-300 hover:shadow-sm transition-all duration-300 cursor-default overflow-hidden"
            >
              <div className="h-44 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-100 border border-orange-200 flex-shrink-0">
                    <i className={`${service.icon} text-orange-600 text-xl`} />
                  </div>
                  <h3 className="font-display font-black text-slate-900 text-base">{service.title}</h3>
                </div>
                <p className="text-slate-500 text-sm leading-relaxed mb-3">{service.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {service.highlights.map((h) => (
                    <span key={h} className="px-2 py-0.5 rounded-full text-xs bg-orange-50 border border-orange-200 text-orange-700 font-semibold">
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Coverage banner */}
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-7 flex flex-col md:flex-row items-center justify-between gap-5">
          <div>
            <p className="text-slate-900 font-display font-black text-xl mb-1">
              Security camera installers near you in NYC
            </p>
            <p className="text-slate-500 text-sm">
              Manhattan · Brooklyn · Queens · The Bronx · Staten Island · Long Island · New Jersey
            </p>
          </div>
          <Link
            to="#contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-slate-900 text-white font-black text-sm hover:bg-slate-800 transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0"
          >
            Get NYC Installation Quote
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
